/**
 * The Review step's one-person test side panel (plan 2026-10-15-003, U5, §6).
 *
 * Three sections (Test first, then Messages, then About), each a reused piece:
 *   1. "Messages for {first name}" — the existing dispatch-payload preview,
 *      rendered by `PayloadBlock`;
 *   2. "Test" — a button that sends a normal test with `testMemberIds: [id]`,
 *      then READS that one log row through the same hooks the Dispatch log
 *      uses (`useSendLogRows`, the already-polled sends query,
 *      `useSendRecheck`) and shows it through `classifyRepStatus` +
 *      `SentCopyPanel`. This file lays out; it never classifies;
 *   3. "About {first name}" — `MemberDetailFields`, the same fields the plain
 *      details drawer shows, then "All of {first name}'s sends" (plan
 *      2026-10-15-004 §6.5), which narrows the Dispatch log's "All people"
 *      table to this person.
 *
 * Opened from a name in that table (`member-test-store`'s `focusByMember`) the
 * result section shows THAT exact row — a real send's or an older test's —
 * under "Real send · when" / "Test · when", instead of the person's latest test.
 *
 * Every derived value is computed at render and the POST runs in a click
 * handler, so there is no effect here for StrictMode to double-fire. Which
 * person is open and each person's latest test live in `member-test-store`
 * (a module-scope zustand store) so the panel survives a tab switch. No id,
 * status code or raw error text ever reaches the panel (R20): request
 * failures go through `memberTestFailureOf`.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import { useApi } from '../../../_shared/hooks';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import {
  fetchSentCopyRequest,
  listPlaySendsRequest,
  previewDispatchPayloadRequest,
} from '../../api-client';
import { playSendsQueryKey } from '../../dispatch-inspector-logic';
import { DISPATCH_LOG_ANCHOR_ID, showOnlyPerson } from '../../dispatch-log-filter-store';
import { REP_STATUS_BADGE, classifyRepStatus } from '../../dispatch-rep-status';
import { memberFirstName, memberTestDisabledReason, memberTestRowOf } from '../../member-test-logic';
import { memberTestKey, useMemberTestStore } from '../../member-test-store';
import { playsPrefixKey } from '../../query-keys';
import type { ReviewableRung } from '../../review-gate';
import type { PlaybooksLibraryResponse } from '../../schemas';
import { channelLabel, notReadyBadgeText } from '../../sequence-readiness';
import { useRungReadiness } from '../../use-rung-readiness';
import { PushDrawer } from '../PushDrawer';

import { MemberDetailFields, SectionTitle } from './MemberDetailFields';
import { memberDisplay } from './MemberRowCells';
import { PayloadBlock } from './dispatch-log/PayloadBlock';
import { formatSentAt } from './dispatch-log/SendLogCard';
import { Banner, SentCopyPanel } from './dispatch-log/SentCopyPanel';
import { sentCopyQueryKey } from './dispatch-log/SendLogRow';
import { usePlayLogRows } from './dispatch-log/usePlayLogRows';
import { useSendLogRows } from './dispatch-log/useSendLogRows';
import { useSendRecheck } from './dispatch-log/useSendRecheck';
import { useMemberTest } from './useMemberTest';

export interface MemberTestDrawerProps {
  /** `undefined` in Playbook mode: the panel shows details only. */
  playId?: string;
  member: CampaignMember;
  /** The person's group, or `null` for "Not in any group". */
  rung: ReviewableRung | null;
  /** The group's 1-based position on the Review card, for the header line. */
  groupNumber: number | null;
  library: PlaybooksLibraryResponse | undefined;
  /** `tally.routedMemberIds`; `null` while the ladder is unavailable. */
  routedMemberIds: string[] | null;
  /** `reviewActionsLocked(pendingAction)`. */
  actionsLocked: boolean;
  onClose: () => void;
}

export function MemberTestDrawer(props: MemberTestDrawerProps) {
  const { member, rung, groupNumber, library, onClose } = props;
  const sequence = rung?.mapping ? library?.sequences.find((s) => s.id === rung.mapping!.sequenceId) : undefined;
  const segmentation = rung ? library?.segmentations.find((s) => s.id === rung.segmentationId) : undefined;
  const groupLine =
    rung === null || groupNumber === null
      ? null
      : [
          `Group ${groupNumber} · ${segmentation?.name ?? 'Segment unavailable'}`,
          rung.mapping ? ` → ${sequence?.name ?? 'Sequence unavailable'}` : '',
          rung.mapping ? ` · ${channelLabel(rung.mapping.channel)}` : '',
        ].join('');

  return (
    <PushDrawer title={member.contact_name ?? 'Member'} subtitle={memberDisplay(member).email || undefined} onClose={onClose}>
      {groupLine !== null && (
        <Text size="xs" variant="tertiary">
          {groupLine}
        </Text>
      )}
      <MemberTestBody {...props} />
      <section className="flex flex-col gap-2">
        <SectionTitle>{`About ${memberFirstName(member.contact_name)}`}</SectionTitle>
        <MemberDetailFields member={member} />
        {props.playId !== undefined && (
          <div>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                showOnlyPerson(member.id, member.contact_name || memberDisplay(member).email || 'This person');
                // The log sits further down the Review step; bring it to the rep.
                document.getElementById(DISPATCH_LOG_ANCHOR_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
              }}
            >
              {`All of ${memberFirstName(member.contact_name)}'s sends`}
            </Button>
          </div>
        )}
      </section>
    </PushDrawer>
  );
}

/** Sections 1 and 2. Split from the shell so Playbook mode (no play) needs no play-scoped hook. */
function MemberTestBody(props: MemberTestDrawerProps) {
  const { playId, rung, routedMemberIds, actionsLocked } = props;
  const { readiness, reasons } = useRungReadiness(rung?.mapping ?? null, props.library);
  const reason = memberTestDisabledReason({
    hasPlay: playId !== undefined,
    inGroup: rung !== null,
    rungMapped: rung?.mapping != null,
    notReadyText: readiness === 'not_ready' && rung?.mapping ? notReadyBadgeText(reasons, rung.mapping.channel) : null,
    tallyAvailable: routedMemberIds !== null,
    actionsLocked,
  });

  if (playId === undefined) {
    return (
      <section className="flex flex-col gap-2">
        <SectionTitle>Test</SectionTitle>
        <Text size="xs" variant="tertiary">
          {reason}
        </Text>
      </section>
    );
  }
  return <PlayMemberTest {...props} playId={playId} disabledReason={reason} />;
}

function PlayMemberTest({
  playId,
  member,
  rung,
  routedMemberIds,
  disabledReason,
}: MemberTestDrawerProps & { playId: string; disabledReason: string | null }) {
  const { apiFetch } = useApi();
  const first = memberFirstName(member.contact_name);
  const { start, pending, latest, disabled, failure } = useMemberTest({
    playId,
    member,
    routedMemberIds,
    disabledReason,
  });
  // The row the panel was opened on (a click on a name in "All people"), if any.
  const focus = useMemberTestStore((state) => state.focusByMember[memberTestKey(playId, member.id)]);
  const latestDispatchId = latest?.dispatchId ?? null;
  const dispatchId = focus?.dispatchId ?? latestDispatchId;

  // Section 1 — the person's messages. Same cache entry as the Dispatch log's preview.
  const hasSequence = rung?.mapping != null;
  const previewQuery = useQuery({
    queryKey: [...playsPrefixKey(), playId, 'dispatch-payload', 'preview', member.id],
    queryFn: () => previewDispatchPayloadRequest(apiFetch, playId, { memberId: member.id }),
    enabled: hasSequence,
  });

  // Section 2 — the live result. The sends query is the one the Dispatch log
  // polls; this read only DISPLAYS the person's one row (the hook decides
  // whether the button is greyed).
  const sendsQuery = useQuery({
    queryKey: playSendsQueryKey(playId),
    queryFn: () => listPlaySendsRequest(apiFetch, playId),
    enabled: dispatchId !== null,
  });
  const summary = (
    sendsQuery.data?.testSends.find((t) => t.dispatchId === dispatchId) ??
    sendsQuery.data?.sends.find((s) => s.dispatchId === dispatchId)
  )?.summary;
  // The latest test may be a "Test selected" batch (one dispatch, up to 100 rows, one page, the same
  // dispatch id recorded for every ticked person), so THIS person's row is found by member id; a
  // focused row is found among THIS person's sends (a real send may have thousands of rows — never page that).
  const latestLog = useSendLogRows({
    playId,
    dispatchId: focus === undefined ? latestDispatchId : null,
    status: 'all',
    enabled: focus === undefined && latestDispatchId !== null,
    live: summary?.live ?? true,
  });
  const focusLog = usePlayLogRows({
    playId,
    source: { kind: 'play', filters: { kind: focus?.isTest === false ? 'real' : 'test', status: 'all', memberId: member.id } },
    enabled: focus !== undefined,
    live: summary?.live ?? true,
  });
  const log = focus === undefined ? latestLog : focusLog;
  const recheck = useSendRecheck(playId, dispatchId, focus === undefined ? (summary?.needsRecheck ?? 0) : 0);
  const row = focus === undefined ? memberTestRowOf(latestLog.rows, member.id) : focusLog.rows.find((r) => r.id === focus.logId);
  const view = row ? classifyRepStatus(row, Date.now()) : null;
  // A focused row older than the first page (a person can have more than a
  // page of rows of one kind) is found by paging on — one page at a time, never
  // while a fetch runs or after one failed, and it ends when the pages do.
  const focusRowMissing = focus !== undefined && row === undefined;
  const { hasNextPage: focusHasMore, isFetchingNextPage: focusFetchingMore, isLoading: focusLoading, isError: focusErrored, loadMore: focusLoadMore } = focusLog;
  useEffect(() => {
    if (focusRowMissing && focusHasMore && !focusFetchingMore && !focusLoading && !focusErrored) focusLoadMore();
  }, [focusRowMissing, focusHasMore, focusFetchingMore, focusLoading, focusErrored, focusLoadMore, focusLog.rows.length]);
  const focusGone = focusRowMissing && !focusHasMore && !focusFetchingMore && !focusLoading && !focusErrored;
  const shownAt = focus === undefined ? latest?.startedAt : (row?.dispatched_at ?? undefined);
  // A real send's row has no "Test again" — its Retry stays in the table, the one place for real-send actions.
  const offerTestAgain = focus === undefined || focus.isTest;
  const sentCopy = useQuery({
    queryKey: sentCopyQueryKey(playId, row?.id ?? '', row?.retry_count ?? 0),
    queryFn: () => fetchSentCopyRequest(apiFetch, playId, row?.id ?? ''),
    enabled: row !== undefined && view?.status === 'delivered',
  });

  return (
    <>
      <section className="flex flex-col gap-2">
        <SectionTitle>Test</SectionTitle>
        <div className="flex flex-col gap-1.5">
          <div>
            <Button variant="primary" size="sm" disabled={disabled} onClick={() => void start()}>
              {pending ? <LoadingSpinner size="sm" /> : <Icon type={PaperPlaneTilt} />}
              {`Send a test for ${first}`}
            </Button>
          </div>
          <Text size="xs" variant="tertiary">
            {disabledReason ??
              `Clay builds ${first}'s messages exactly like a real send and sends nothing. Usually takes under a minute.`}
          </Text>
        </div>

        {failure !== null && (
          <Alert status={failure.status === 'info' ? 'info' : 'error'} disableErrorLogging>
            {failure.content}
          </Alert>
        )}

        {focusGone && (
          <Text size="xs" variant="tertiary">
            This result is no longer available.
          </Text>
        )}
        {dispatchId !== null && view === null && !log.isError && !focusGone && (
          <div className="flex items-center gap-2">
            <LoadingSpinner size="sm" />
            <Text size="xs" variant="tertiary">
              {focus === undefined ? 'Starting the test…' : 'Loading this result…'}
            </Text>
          </div>
        )}
        {dispatchId !== null && log.isError && (
          <Text size="xs" variant="tertiary">
            {focus === undefined
              ? "Couldn't load the test result. Try again in a minute."
              : "Couldn't load this result. Try again in a minute."}
          </Text>
        )}

        {view !== null && shownAt !== undefined && (
          <div className="flex flex-col gap-2">
            {focus !== undefined && (
              <Text size="xs" weight="medium" variant="tertiary">
                {`${focus.isTest ? 'Test' : 'Real send'} · ${formatSentAt(shownAt)}`}
              </Text>
            )}
            {view.status === 'in_progress' ? (
              <div className="flex flex-col gap-1.5">
                <span data-rep-status={view.status}>
                  <StatusBadge status={REP_STATUS_BADGE[view.status]} size="sm">
                    {view.pillText}
                  </StatusBadge>
                </span>
                {view.banner !== null && <Banner banner={view.banner} />}
                <Text size="xs" variant="tertiary">
                  {`Started ${formatSentAt(shownAt)}`}
                </Text>
              </div>
            ) : view.status === 'delivered' ? (
              <SentCopyPanel query={sentCopy} view={view} />
            ) : (
              <div className="flex flex-col gap-1.5">
                {view.banner !== null && <Banner banner={view.banner} />}
                {view.banner?.text !== view.result && (
                  <Text size="xs" variant="tertiary">
                    {view.result}
                  </Text>
                )}
              </div>
            )}

            {view.needsRecheck && (
              <div className="flex flex-col gap-1">
                <Text size="xs" variant="tertiary">
                  Clay hasn&apos;t answered yet.
                </Text>
                <div>
                  <Button variant="secondary" size="xs" disabled={recheck.checking} onClick={recheck.checkAgain}>
                    {recheck.checking && <LoadingSpinner size="sm" />}
                    Check again
                  </Button>
                </div>
              </div>
            )}

            {view.status !== 'in_progress' && offerTestAgain && (
              <div>
                <Button
                  variant="secondary"
                  size="xs"
                  disabled={disabled}
                  onClick={() => void start()}
                >
                  Test again
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      {hasSequence && (
        <section className="flex flex-col gap-2">
          <SectionTitle>{`Messages for ${first}`}</SectionTitle>
          <div className="rounded-md border border-border-secondary">
            <PayloadBlock query={previewQuery} showRawJson={false} hosted hidePaddingSteps />
          </div>
        </section>
      )}
    </>
  );
}
